import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchGalleryImages } from '../services/galleryService';
import type { GalleryImage } from '../types/gallery';

export interface UseGalleryImagesOptions {
  aquariumId: string;
  limit?: number;
  enabled?: boolean;
  albumId?: string | null;
  tagId?: string | null;
}

export interface UseGalleryImagesResult {
  images: GalleryImage[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  error: Error | null;
  /** Re-runs the query (e.g. the "Retry" of a load error). */
  refetch?: () => void;
}

export function useGalleryImages(options: UseGalleryImagesOptions): UseGalleryImagesResult {
  const { aquariumId, limit = 24, enabled = true, albumId = null, tagId = null } = options;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery({
    // Filters are part of the key; ['galleryImages', aquariumId] invalidates every variant.
    queryKey: ['galleryImages', aquariumId, limit, albumId, tagId],
    queryFn: ({ pageParam }) =>
      fetchGalleryImages({ aquariumId, page: pageParam as number, limit, albumId, tagId }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    enabled: enabled && Boolean(aquariumId),
  });

  const images: GalleryImage[] = data?.pages.flatMap((page) => page.images) ?? [];

  return {
    images,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    error: error as Error | null,
    refetch: () => {
      void refetch();
    },
  };
}
